import {
  CompanyContractPlan,
  type AppRoleEnum,
  type FeatureNameEnum,
} from '@ulysses-inc/harami_api_client'
import { useEnabledFeatures } from '~/domain/featureFlags/hooks/useFeatureFlags'
import { selectContractPlan, useSessionStore } from '~/stores/session/session'

/**
 * 機能の利用可否を判定する際の単位となるもの。
 *
 * 新規に「契約プラン × Feature Flag × ロール」で開放制御したい機能を追加する場合は、
 * このユニオン型と下記の `settings` カタログにエントリを足し、呼び出し側で
 * `useLimitedFeatureAvailability` を使って判定する。個別 hook を生やさない。
 */
export type Feature = 'improves' | 'multiLanguageSupport' | 'taskSchedule'

/**
 * taskSchedule は @ulysses-inc/harami_api_client の FeatureNameEnum にまだ
 * 含まれていない。クライアントに載ったら FeatureNameEnum の値に差し替える。
 */
const taskScheduleFlag = 'taskSchedule' as FeatureNameEnum

/**
 * ある機能を利用するにあたって満たすべき条件。条件は AND で結合される。
 * - 何も指定しなければ「常に利用可能」
 * - eligibleFeatureFlag: 指定された Feature Flag が ON である必要がある
 * - eligiblePlans: 列挙されたいずれかの契約プランである必要がある
 * - eligibleRoles: 列挙されたいずれかのロールである必要がある
 */
type Setting = {
  eligibleFeatureFlag?: FeatureNameEnum
  eligiblePlans?: readonly CompanyContractPlan[]
  eligibleRoles?: readonly AppRoleEnum[]
}

const settings: Record<Feature, Setting> = {
  // カイゼン Web (指摘の作成・編集)
  // OLD_BASIC は旧契約者の互換のため PREMIUM と同等に利用可とする
  improves: {
    eligiblePlans: [CompanyContractPlan.PREMIUM, CompanyContractPlan.OLD_BASIC],
  },
  // 多言語対応 (言語選択画面)
  multiLanguageSupport: {
    eligiblePlans: [CompanyContractPlan.PREMIUM],
  },
  // 作業指示から計算した予定を扱う機能
  taskSchedule: {
    eligibleFeatureFlag: taskScheduleFlag,
  },
}

/**
 * unknown は plan/role を満たしつつ Feature Flag 取得中で確定できない状態を表す。
 * (plan/role が満たされていない場合は flag を待たず `'no'` を返す)
 */
export type FeatureAvailability = 'yes' | 'no' | 'unknown'

/**
 * Plan/Role 起因と Feature Flag 起因の判定結果を分けて返す型。
 * ルートガードで「plan/role 拒否は常にリダイレクト、Feature Flag 拒否は
 * local 環境ではバイパス」を表現するために使う。
 */
export type FeatureAvailabilityDetail = {
  planRole: FeatureAvailability
  flag: FeatureAvailability
}

/**
 * 限定されたユーザーにのみ開放している機能の利用可否を、契約プラン・ロール・
 * Feature Flag を AND で評価して返すカスタムフック。
 *
 * 「機能と要件の対応」を `settings` に一元管理することで、利用箇所ごとに plan や
 * role を見落とすケースを防ぐ。harami_web の同名フックと仕組みを揃えている。
 */
/**
 * plan / role の判定結果を `FeatureAvailability` 形で返す。
 * - 要件 (eligible) なし → 'yes' (常に満たす)
 * - 要件あり + 取得済の値が一致 → 'yes'
 * - 要件あり + 取得済の値が不一致 → 'no'
 * - 要件あり + 値が未取得 → 'unknown' (ロード中なので確定できない)
 *
 * `selectContractPlan` のコメントにある通り `undefined` は「未取得」を意味し、
 * 「非 PREMIUM」とみなして弾くと初期描画でリダイレクトされる事故になるため、
 * unknown として描画を保留する。
 */
const evaluateEligibility = <T>(
  eligible: readonly T[] | undefined,
  current: T | undefined,
): FeatureAvailability => {
  if (eligible === undefined) return 'yes'
  if (current === undefined) return 'unknown'
  return eligible.includes(current) ? 'yes' : 'no'
}

export const useLimitedFeatureAvailability = () => {
  const { features: enabledFeatures, isLoading: isFeatureFlagsLoading } =
    useEnabledFeatures()
  const contractPlan = useSessionStore(selectContractPlan)
  // Device 認証時は role を持たない（RBAC 対象外）ため undefined
  const userRole = useSessionStore(state => state.principal?.user?.role)

  const getFeatureAvailabilityDetail = (
    feature: Feature,
  ): FeatureAvailabilityDetail => {
    const { eligiblePlans, eligibleRoles, eligibleFeatureFlag } =
      settings[feature]

    const planAvailability = evaluateEligibility(eligiblePlans, contractPlan)
    const roleAvailability = evaluateEligibility(eligibleRoles, userRole)
    const planRole: FeatureAvailability =
      planAvailability === 'no' || roleAvailability === 'no'
        ? 'no'
        : planAvailability === 'unknown' || roleAvailability === 'unknown'
          ? 'unknown'
          : 'yes'

    const flag: FeatureAvailability =
      eligibleFeatureFlag === undefined
        ? 'yes'
        : enabledFeatures.has(eligibleFeatureFlag)
          ? 'yes'
          : isFeatureFlagsLoading
            ? 'unknown'
            : 'no'

    return { planRole, flag }
  }

  const getFeatureAvailability = (feature: Feature): FeatureAvailability => {
    const { planRole, flag } = getFeatureAvailabilityDetail(feature)
    // 'no' が確定しているときはローディング状態より優先して 'no'
    if (planRole === 'no') return 'no'
    // 要件あり & 値未取得 → 描画保留 ('no' で確定リダイレクトしない)
    if (planRole === 'unknown') return 'unknown'
    return flag
  }

  const isFeatureAvailable = (feature: Feature): boolean =>
    getFeatureAvailability(feature) === 'yes'

  return {
    isFeatureAvailable,
    getFeatureAvailability,
    getFeatureAvailabilityDetail,
  }
}
